import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Plus, Trash2, Send } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";
import { whatsappSend } from "@/lib/pdf";
import FileUploader from "@/components/app/FileUploader";

export default function Settings() {
  const pinPrompt = usePinPrompt();
  const [firm, setFirm] = useState(null);
  const [accs, setAccs] = useState([]);
  const [newAcc, setNewAcc] = useState({ username: "", password: "", company_id: "" });
  const [companies, setCompanies] = useState([]);
  const [newCo, setNewCo] = useState({ name: "", gstin: "", address: "", phone: "", email: "", state: "" });

  const load = async () => {
    const [me, a, c] = await Promise.all([
      api.get("/firms/me"),
      api.get("/firms/accountants"),
      api.get("/companies"),
    ]);
    setFirm(me.data); setAccs(a.data); setCompanies(c.data);
    if (!newAcc.company_id && (c.data || []).length > 0) {
      setNewAcc((x) => ({ ...x, company_id: c.data[0]._id_str }));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await pinPrompt();
    try {
      await api.patch("/firms/settings", {
        firm_name: firm.firm_name, contact: firm.contact, email: firm.email,
        address: firm.address, state: firm.state, gstin: firm.gstin,
        upi_id: firm.upi_id, bank_details: firm.bank_details,
        loyalty: firm.loyalty,
        invoice_template: firm.invoice_template || "classic",
      });
      toast.success("Settings saved");
      localStorage.setItem("firm_name", firm.firm_name);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addAcc = async () => {
    if (!newAcc.username || !newAcc.password) { toast.error("Fill both fields"); return; }
    if (!newAcc.company_id) { toast.error("Pick a company for this accountant"); return; }
    if (accs.length >= 10) { toast.error("Max 10 accountants"); return; }
    await pinPrompt();
    try {
      await api.post("/firms/accountants", newAcc);
      setNewAcc({ username: "", password: "", company_id: newAcc.company_id });
      toast.success("Accountant added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rmAcc = async (u) => {
    if (!window.confirm("Remove " + u + "?")) return;
    await pinPrompt();
    try { await api.delete(`/firms/accountants/${u}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const addCompany = async () => {
    if (!newCo.name) { toast.error("Enter company name"); return; }
    await pinPrompt();
    try {
      await api.post("/companies", newCo);
      setNewCo({ name: "", gstin: "", address: "", phone: "", email: "", state: "" });
      toast.success("Company added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rmCompany = async (id) => {
    if (!window.confirm("Delete this company? It must have no data.")) return;
    await pinPrompt();
    try { await api.delete(`/companies/${id}`); toast.success("Company deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runReminders = async () => {
    try {
      // Compute reminders client-side and open free WhatsApp for each party
      const parties = (await api.get("/parties")).data;
      let count = 0;
      for (const p of parties) {
        if (!p.phone) continue;
        const stmt = (await api.get(`/parties/${p._id_str}/statement`)).data;
        if (Math.abs(stmt.net) < 0.01) continue;
        const body = stmt.net > 0
          ? `Dear ${p.name}, gentle reminder: ₹${stmt.net} is pending. Please arrange payment at your convenience. — ANV ASSOCIATE 8446463026`
          : `Dear ${p.name}, we have ₹${Math.abs(stmt.net)} payable to you. We will process it shortly. — ANV ASSOCIATE 8446463026`;
        whatsappSend(p.phone, body);
        count++;
        if (count >= 5) { toast.info(`Opened 5 chat tabs. Reload to send more.`); break; }
      }
      if (count === 0) toast.success("No pending balances — no reminders needed.");
      else toast.success(`Opened ${count} WhatsApp chats.`);
    } catch (e) { toast.error("Failed to run reminders"); }
  };

  if (!firm) return <div>Loading…</div>;

  return (
    <div>
      <div className="overline">Firm Settings</div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Settings</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="overline mb-3">Firm details</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Firm Name</Label><Input value={firm.firm_name || ""} onChange={(e) => setFirm({ ...firm, firm_name: e.target.value })} data-testid="settings-firm-name" /></div>
            <div><Label>Contact</Label><Input value={firm.contact || ""} onChange={(e) => setFirm({ ...firm, contact: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={firm.email || ""} onChange={(e) => setFirm({ ...firm, email: e.target.value })} /></div>
            <div><Label>State (for GST split)</Label><Input value={firm.state || ""} onChange={(e) => setFirm({ ...firm, state: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={firm.address || ""} onChange={(e) => setFirm({ ...firm, address: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={firm.gstin || ""} onChange={(e) => setFirm({ ...firm, gstin: e.target.value })} /></div>
            <div><Label>UPI ID</Label><Input value={firm.upi_id || ""} onChange={(e) => setFirm({ ...firm, upi_id: e.target.value })} data-testid="settings-upi" /></div>
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <div className="overline mb-2">Bank details (for invoices)</div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Bank name" value={firm.bank_details?.name || ""} onChange={(e) => setFirm({ ...firm, bank_details: { ...(firm.bank_details || {}), name: e.target.value } })} />
              <Input placeholder="Account number" value={firm.bank_details?.account_number || ""} onChange={(e) => setFirm({ ...firm, bank_details: { ...(firm.bank_details || {}), account_number: e.target.value } })} />
              <Input placeholder="IFSC" value={firm.bank_details?.ifsc || ""} onChange={(e) => setFirm({ ...firm, bank_details: { ...(firm.bank_details || {}), ifsc: e.target.value } })} />
              <Input placeholder="Branch" value={firm.bank_details?.branch || ""} onChange={(e) => setFirm({ ...firm, bank_details: { ...(firm.bank_details || {}), branch: e.target.value } })} />
            </div>
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <div className="overline mb-2">Loyalty program</div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Points per ₹</Label><Input type="number" value={firm.loyalty?.points_per_rupee ?? 0} onChange={(e) => setFirm({ ...firm, loyalty: { ...(firm.loyalty || {}), points_per_rupee: +e.target.value } })} data-testid="settings-loyalty-rate" /></div>
              <div><Label>Redeem rate (₹/pt)</Label><Input type="number" value={firm.loyalty?.redeem_rate ?? 0} onChange={(e) => setFirm({ ...firm, loyalty: { ...(firm.loyalty || {}), redeem_rate: +e.target.value } })} /></div>
              <div><Label>Min redeem pts</Label><Input type="number" value={firm.loyalty?.min_redeem ?? 0} onChange={(e) => setFirm({ ...firm, loyalty: { ...(firm.loyalty || {}), min_redeem: +e.target.value } })} /></div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Loyalty applies to sales and POS totals. Configure and adjust anytime.</div>
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <div className="overline mb-2">Invoice Template</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { k: "classic", label: "Classic", hint: "Elegant serif" },
                { k: "modern", label: "Modern", hint: "Green header" },
                { k: "minimal", label: "Minimal", hint: "No borders" },
                { k: "thermal", label: "Thermal", hint: "80mm receipt" },
              ].map((tpl) => (
                <button
                  key={tpl.k}
                  onClick={() => setFirm({ ...firm, invoice_template: tpl.k })}
                  data-testid={`template-pick-${tpl.k}`}
                  className={`border rounded-md p-3 text-left transition-colors ${
                    (firm.invoice_template || "classic") === tpl.k
                      ? "border-[#064E3B] bg-[#ECFDF5]"
                      : "border-border hover:bg-slate-50"
                  }`}
                >
                  <div className="font-semibold text-sm">{tpl.label}</div>
                  <div className="text-[10px] text-muted-foreground">{tpl.hint}</div>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Currently: <b>{(firm.invoice_template || "classic").toUpperCase()}</b>. Click Save to apply to future invoice prints.
            </div>
          </div>
          <Button className="btn-primary mt-4" onClick={save} data-testid="settings-save"><Save size={14} /> Save changes</Button>

          <div className="mt-6 grid gap-4">
            <div className="text-[11px] text-muted-foreground bg-[#ECFDF5] border border-[#A7F3D0] rounded p-2">
              💡 Logo & Signature below apply to the <b>currently selected company</b> ({localStorage.getItem("company_name") || "Main"}).
              Switch companies from the header to configure another one.
            </div>
            <FileUploader kind="logo" multiple={false} label="Company Logo (shown on invoices)" accept="image/png,image/jpeg,image/webp" />
            <FileUploader kind="signature" multiple={false} label="Authorised Signature (embedded on invoices)" accept="image/png,image/jpeg,image/webp" />
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Companies (multi-company)</div>
            {firm?.plan && (
              <span className={`chip ${firm.plan === "business" ? "chip-green" : "chip-amber"} uppercase`}>
                {firm.plan === "business" ? "Business" : `${firm.plan} plan · 1 company only`}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {companies.map((c) => (
              <div key={c._id_str} className="flex items-center justify-between border border-border rounded-md p-2 text-sm" data-testid={`company-row-${c._id_str}`}>
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    {c.gstin || "—"} {c.state ? `· ${c.state}` : ""}
                  </div>
                </div>
                <button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => rmCompany(c._id_str)} data-testid={`company-del-${c._id_str}`}><Trash2 size={14} /></button>
              </div>
            ))}
            {companies.length === 0 && <div className="text-xs text-muted-foreground">No companies yet. Your firm's Main Company will be created automatically.</div>}
          </div>
          {firm?.plan === "business" ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Input placeholder="Company name*" value={newCo.name} onChange={(e) => setNewCo({ ...newCo, name: e.target.value })} data-testid="company-new-name" />
              <Input placeholder="GSTIN" value={newCo.gstin} onChange={(e) => setNewCo({ ...newCo, gstin: e.target.value })} data-testid="company-new-gstin" />
              <Input placeholder="Phone" value={newCo.phone} onChange={(e) => setNewCo({ ...newCo, phone: e.target.value })} />
              <Input placeholder="State" value={newCo.state} onChange={(e) => setNewCo({ ...newCo, state: e.target.value })} />
              <Input className="col-span-2" placeholder="Address" value={newCo.address} onChange={(e) => setNewCo({ ...newCo, address: e.target.value })} />
              <Button className="btn-primary col-span-2" onClick={addCompany} data-testid="company-add"><Plus size={14} /> Add Company</Button>
            </div>
          ) : (
            <div className="mt-3 border-2 border-dashed border-amber-300 bg-amber-50 rounded-md p-4 text-center" data-testid="company-add-locked">
              <div className="text-3xl mb-1">🔒</div>
              <div className="font-bold text-amber-900">Multi-Company is a Business-plan feature</div>
              <div className="text-xs text-amber-800 mt-1">Upgrade to add more companies with fully-isolated data. Currently on <b className="uppercase">{firm?.plan || "professional"}</b>.</div>
              <a href="/pricing" className="inline-block mt-3 bg-[#064E3B] text-white px-4 py-1.5 rounded-md text-sm font-semibold">Upgrade Plan →</a>
            </div>
          )}
        </div>

        <div className="bg-white border border-border rounded-lg p-5">
          <div className="overline mb-3">Accountants (max 10)</div>
          <div className="space-y-2">
            {accs.map((a) => {
              const co = companies.find((c) => c._id_str === a.company_id);
              return (
                <div key={a.username} className="flex items-center justify-between border border-border rounded-md p-2 text-sm">
                  <div>
                    <div className="font-semibold">{a.username}</div>
                    <div className="text-[10px] text-slate-500">{co?.name || "—"}</div>
                  </div>
                  <button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => rmAcc(a.username)} data-testid={`acc-del-${a.username}`}><Trash2 size={14} /></button>
                </div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Input placeholder="Username" value={newAcc.username} onChange={(e) => setNewAcc({ ...newAcc, username: e.target.value })} data-testid="acc-new-username" />
            <Input placeholder="Password" type="password" value={newAcc.password} onChange={(e) => setNewAcc({ ...newAcc, password: e.target.value })} data-testid="acc-new-password" />
            <select
              className="col-span-2 border border-border rounded-md h-9 px-2 bg-white text-sm"
              value={newAcc.company_id}
              onChange={(e) => setNewAcc({ ...newAcc, company_id: e.target.value })}
              data-testid="acc-new-company"
            >
              <option value="">— assign to which company —</option>
              {companies.map((c) => <option key={c._id_str} value={c._id_str}>{c.name}</option>)}
            </select>
            <Button className="btn-primary col-span-2" onClick={addAcc} data-testid="acc-add"><Plus size={14} /> Add Accountant</Button>
          </div>

          <div className="border-t border-border mt-6 pt-4">
            <div className="overline mb-2">10-Day Reminders (Free WhatsApp)</div>
            <div className="text-sm text-muted-foreground">Opens a WhatsApp Web / mobile chat tab per pending party with a pre-filled reminder — send with one tap. No SMS charges.</div>
            <Button variant="outline" className="mt-2" onClick={runReminders} data-testid="run-reminders"><Send size={14} /> Open reminder chats</Button>
          </div>

          <OtpModeSection firm={firm} onChange={load} />
        </div>
      </div>
    </div>
  );
}

function OtpModeSection({ firm, onChange }) {
  const pinPrompt = usePinPrompt();
  const [require_otp, setReq] = useState(!!firm.require_otp);
  const [phone, setPhone] = useState(firm.owner_phone || firm.contact || "");
  const [status, setStatus] = useState(null);
  const [newPin, setNewPin] = useState("");
  useEffect(() => { (async () => setStatus((await api.get("/otp/status")).data))(); }, []);
  const save = async () => {
    await pinPrompt();
    try {
      await api.post("/settings/otp-mode", { require_otp, owner_phone: phone });
      toast.success("Settings updated"); onChange();
    } catch (e) { toast.error("Failed"); }
  };
  const changePin = async () => {
    if (!newPin || newPin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    await pinPrompt();
    try {
      await api.post("/firms/change-edit-pin", { new_pin: newPin });
      toast.success("Transaction PIN updated");
      setNewPin("");
      // clear cached edit_pin so next action forces re-entry with new PIN
      localStorage.removeItem("edit_pin");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border-t border-border mt-6 pt-4">
      <div className="overline mb-2">Transaction PIN</div>
      <div className="text-sm text-muted-foreground">The PIN required for every edit/new entry.</div>
      <div className="grid grid-cols-3 gap-3 mt-2">
        <div className="col-span-2">
          <Label>New PIN (4-6 digits)</Label>
          <Input type="password" maxLength={6} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, ""))} data-testid="settings-new-pin" />
        </div>
        <div className="flex items-end">
          <Button variant="outline" onClick={changePin} data-testid="settings-change-pin">Change PIN</Button>
        </div>
      </div>

      <div className="overline mt-6 mb-2">Click-to-chat</div>
      <div className="text-sm text-muted-foreground">
        All WhatsApp messages open in your browser via free wa.me links. No API charges.
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div className="col-span-2">
          <Label>Owner phone (E.164, e.g. +919999999999)</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="settings-owner-phone" />
        </div>
      </div>
      <Button variant="outline" className="mt-3" onClick={save} data-testid="settings-otp-save">Save</Button>
    </div>
  );
}
